<script setup lang="ts">
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  CheckBadgeIcon,
  BellIcon,
  ChevronUpDownIcon,
  ArrowRightOnRectangleIcon,
  WrenchScrewdriverIcon,
  TableCellsIcon,
} from '@heroicons/vue/24/solid'
import { ref, computed } from 'vue';
import BugReportDialog from '@/components/BugReport/BugReportDialog.vue';
import { getAccessToken } from '@/services/tokenStorage';

withDefaults(defineProps<{
  user: {
    name?: string
    initials?: string
    email?: string
    is_developer?: boolean
  }
  /** Show only the avatar, for the icon strip. */
  compact?: boolean
}>(), {
  compact: false,
})

const appVersion = computed(() => __APP_VERSION__);
const apiUrl = computed(() => import.meta.env.VITE_API_URL);
const horizonUrl = computed(() => {
  const token = getAccessToken();
  const base = `${apiUrl.value}/horizon-login`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
});
const { isMobile } = useSidebar()
const isBugReportDialogOpen = ref(false);
</script>

<template>
  <!-- Compact, the list shrinks to the avatar, so it centres in the icon strip
       like the buttons above it instead of sitting at the list's left edge. -->
  <SidebarMenu :class="compact && 'w-auto items-center'">
    <SidebarMenuItem :class="compact && 'w-auto'">
      <DropdownMenu>
        <DropdownMenuTrigger v-if="compact" as-child>
          <!-- In the icon strip the menu opens from the avatar alone. -->
          <button
            type="button"
            :aria-label="`Account menu for ${user.name}`"
            :title="user.name"
            class="flex size-10 items-center justify-center rounded-xl hover:bg-sidebar-accent data-[state=open]:bg-sidebar-accent"
          >
            <Avatar class="size-7 rounded-full">
              <AvatarImage src="" :alt="user.name" />
              <AvatarFallback class="rounded-full text-[11px] font-semibold">
                {{ user.initials }}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuTrigger v-else as-child>
          <SidebarMenuButton
            size="lg"
            class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <Avatar class="h-8 w-8 rounded-lg">
              <AvatarImage src="" :alt="user.name" />
              <AvatarFallback class="rounded-lg">
                {{ user.initials }}
              </AvatarFallback>
            </Avatar>
            <div class="grid flex-1 text-left text-sm leading-tight">
              <span class="truncate font-medium">{{ user.name }}</span>
              <span class="truncate text-xs">{{ user.email }}</span>
            </div>
            <ChevronUpDownIcon class="ml-auto size-4" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          class="w-[--reka-dropdown-menu-trigger-width] min-w-56 rounded-lg"
          :side="isMobile ? 'bottom' : 'right'"
          align="end"
          :side-offset="4"
        >
          <DropdownMenuLabel class="p-0 font-normal">
            <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar class="h-8 w-8 rounded-lg">
                <AvatarImage src="" :alt="user.name" />
                <AvatarFallback class="rounded-lg">
                  {{ user.initials }}
                </AvatarFallback>
              </Avatar>
              <div class="grid flex-1 text-left text-sm leading-tight">
                <span class="truncate font-semibold">{{ user.name }}</span>
                <span class="truncate text-xs">{{ user.email }}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <router-link to="/profile" as-child>
              <DropdownMenuItem>
                <CheckBadgeIcon />
                Profile
              </DropdownMenuItem>
            </router-link>
            <DropdownMenuItem @click="isBugReportDialogOpen = true">
              <BellIcon />
              Report Bug
            </DropdownMenuItem>
            <DropdownMenuItem asChild v-if="user.is_developer">
              <a :href="horizonUrl" target="_blank" rel="noopener noreferrer" class="flex items-center gap-2">
                <WrenchScrewdriverIcon class="w-4 h-4" />
                Horizon
              </a>
            </DropdownMenuItem>
            <router-link v-if="user.is_developer" to="/dynamo-table-stats" as-child>
              <DropdownMenuItem>
                <TableCellsIcon />
                DynamoDB stats
              </DropdownMenuItem>
            </router-link>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <router-link to="/account/logout" as-child>
            <DropdownMenuItem>
              <ArrowRightOnRectangleIcon />
              Log out
            </DropdownMenuItem>
          </router-link>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled>
            <span class="text-xs text-gray-500 select-none">v{{ appVersion }}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  </SidebarMenu>

  <BugReportDialog 
    :user="user"
    v-model:open="isBugReportDialogOpen"
  />
</template>
